import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Box, Grid, Typography, Modal, Skeleton } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { LoadingButton } from '@mui/lab';
import { FileDownloadOutlined } from '@mui/icons-material';
import { MultiSelectWithSearch } from 'src/components/multi-select-with-search';
import SucessModal from 'src/components/sucess-modal';
import { getUB04BatchRetrieve } from 'src/services/serviceLogMatrixService/getUB04BatchRetrieve';
import { getPullUB04ServiceLog } from 'src/services/serviceLogMatrixService/getPullUB04ServiceLog';
import {
  IServiceLogGetParams,
  TRetrieveBatchGetParams,
} from 'src/services/serviceLogMatrixService/types';
import _ from 'lodash';
import moment from 'moment';

function Ubo4Download() {
  const getFacilityUuid = localStorage.getItem('facilityId');

  const [defaultDate, setDefaultDate] = useState<Date>(new Date());
  const [dateToApiCall, setDateToApiCall] = useState<{ year: string; month: string }>({
    year: '',
    month: '',
  });
  const [searchString, setSearchString] = useState<string>('');
  const [patientsParam, setPatientsParam] = useState<string>('');
  const [isAllPatientsSelected, setIsAllPatientsSelected] = useState<boolean>(false);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [snackbarError, setSnackbarError] = useState<boolean>(false);
  const [snackbarSuccess, setSnackbarSuccess] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    title: '',
  });
  const [facilityName, setFacilityName] = useState<string>('');
  const [patientsFromBatch, setPatientsFromBatch] = useState<{ label: string; value: unknown }[]>(
    []
  );
  const [lastUpdatedDate, setLastUpdatedDate] = useState<string | Date | null>(new Date());
  // Pagination state (match ServiceLogMatrix)
  const PAGE_SIZE = 30;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [retrieveBatchLoading, setRetrieveBatchLoading] = useState<boolean>(false);
  const [resetValues, setResetValues] = useState<boolean>(false);
  const [patientValues, setPatientValues] = useState<unknown[]>([]);
  // Separate loading state for search to show accurate feedback
  const [searchLoading, setSearchLoading] = useState<boolean>(false);

  const getAssignedFacilities = useSelector((state: any) => state.getFacilityDropDownReducer);

  const handleChangeDate = (date: Date | null) => {
    setPatientsParam('');
    setIsAllPatientsSelected(false);
    setSearchString('');
    // Reset pagination and data
    setTotalItems(0);
    setCurrentPage(1);
    setPatientsFromBatch([]);
    setResetValues(true);

    const check = moment(date, 'YYYY/MM/DD');
    const newDate = { month: check.format('M'), year: check.format('YYYY') };

    setDateToApiCall({ ...newDate });
  };

  const getDefaultDate = useCallback(() => {
    // Get default date (previous month):
    const previousMonth = new Date();
    previousMonth.setDate(1);
    previousMonth.setMonth(previousMonth.getMonth() - 1);

    setDefaultDate(previousMonth);

    const dateToApi = moment(new Date()).subtract(1, 'months').endOf('month');

    const newDate = { month: dateToApi.format('M'), year: dateToApi.format('YYYY') };
    setDateToApiCall({ ...newDate });
  }, []);

  // Retrieve Batch Endpoint - Same as Service Log Matrix
  const getBatch = useCallback(
    (
      year: string,
      month: string,
      search?: string,
      page?: number | string,
      limit?: number | string
    ) => {
      const batchRequestParams: TRetrieveBatchGetParams = {
        year,
        month,
        enterprise_name: '',
      };
      if (search?.length) batchRequestParams.search = search;
      if (page) batchRequestParams.page = page;
      if (limit) batchRequestParams.limit = limit;

      // Set appropriate loading state
      if (page === 1) {
        setSearchLoading(true);
      } else {
        setRetrieveBatchLoading(true);
      }

      getUB04BatchRetrieve(batchRequestParams)
        .then((response) => {
          const lastModified = moment.utc(response.data.batch_information.modified);

          const localMoment = lastModified.local();

          const formattedLocalTime = localMoment.format('MM/DD/YYYY, h:mm A');

          setLastUpdatedDate(formattedLocalTime);

          const patients = Object.values(response.data.egress_information).map((patient: any) => {
            return {
              label: `${patient.patient_information.last_name}, ${patient.patient_information.first_name} (${patient.patient_information.medical_record_number})`,
              value: patient.patient_information.id,
            };
          });

          // Update total and current page from server response
          const apiPage = Number(response.data.pagination?.page ?? 1);
          const apiTotal = Number(response.data.pagination?.total ?? 0);
          setTotalItems(apiTotal);
          setCurrentPage(apiPage);

          // Append on subsequent pages, replace on first page
          setPatientsFromBatch((prev) => {
            if (apiPage > 1) {
              // Avoid duplicates by id
              const existingIds = new Set(prev.map((p) => String(p.value)));
              const toAdd = patients.filter((p) => !existingIds.has(String(p.value)));
              return [...prev, ...toAdd];
            }
            return patients;
          });

          setSnackbarError(false);
        })
        .catch((error) => {
          setSnackbarSuccess(false);
          if (error.response?.status !== 404) {
            setSnackbarError(true);

            setSnackbar((snackbar) => ({
              ...snackbar,
              open: true,
              title: '',
              message:
                'Something went wrong with retrieving the UB04 data. Please refresh or let our support team know if the issue persists.',
            }));
          }
          setPatientsFromBatch([]);
          setPatientsParam('');
          setIsAllPatientsSelected(false);
        })
        .finally(() => {
          // Clear appropriate loading state
          if (page === 1) {
            setSearchLoading(false);
          } else {
            setRetrieveBatchLoading(false);
          }
          setResetValues(false);
        });
    },
    []
  );

  // Create a stable debounced search function
  const debouncedSearchRef = useRef(
    _.debounce((searchTerm: string, year: string, month: string) => {
      // Only search if search term is at least 2 characters or empty
      const processedSearchTerm = searchTerm?.trim()?.split('-')?.join('');

      // If search term is less than 2 characters and not empty, don't search
      if (processedSearchTerm && processedSearchTerm.length < 2) {
        return;
      }

      // Always first page on new search
      getBatch(year, month, processedSearchTerm, 1, PAGE_SIZE);
    }, 500)
  );

  // Update the debounced function when getBatch changes
  useEffect(() => {
    debouncedSearchRef.current = _.debounce((searchTerm: string, year: string, month: string) => {
      const processedSearchTerm = searchTerm?.trim()?.split('-')?.join('');

      if (processedSearchTerm && processedSearchTerm.length < 2) {
        return;
      }

      getBatch(year, month, processedSearchTerm, 1, PAGE_SIZE);
    }, 500);
  }, [getBatch]);

  useEffect(() => {
    getDefaultDate();
  }, [getDefaultDate]);

  // Debounced search effect (search only)
  useEffect(() => {
    // Do not trigger search until we have valid year and month
    if (!dateToApiCall?.year || !dateToApiCall?.month) return;

    // Reset pagination and data when search changes
    setCurrentPage(1);
    setTotalItems(0);

    // If search is cleared empty, fetch immediately without debounce
    if (!searchString || searchString.trim().length === 0) {
      debouncedSearchRef.current.cancel();
      getBatch(dateToApiCall.year, dateToApiCall.month, undefined, 1, PAGE_SIZE);
    } else {
      debouncedSearchRef.current(searchString, dateToApiCall.year, dateToApiCall.month);
    }

    return () => {
      debouncedSearchRef.current.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString]);

  // Initial load effect - trigger first search when component mounts
  useEffect(() => {
    if (dateToApiCall?.year && dateToApiCall?.month) {
      // Load first page immediately
      getBatch(dateToApiCall.year, dateToApiCall.month, undefined, 1, PAGE_SIZE);
    }
  }, [dateToApiCall?.year, dateToApiCall?.month, getBatch]);

  const handleDownload = () => {
    setDownloadLoading(true);

    const requestParams: IServiceLogGetParams = {
      year: dateToApiCall?.year ?? '',
      month: dateToApiCall?.month ?? '',
      enterprise_name: '',
    };

    if (
      (!isAllPatientsSelected && patientValues && patientValues.length > 0) ||
      (isAllPatientsSelected && searchString.length > 0)
    ) {
      // Get only the selected patients from patientsParam
      const selectedIds = patientsParam.split(',').filter((id) => id.trim() !== '');
      if (selectedIds.length > 0) {
        requestParams.patients_uuids = selectedIds;
      }
    }

    getPullUB04ServiceLog(requestParams)
      .then((res: any) => {
        const trimmedFacilityName = facilityName.trim();
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `${trimmedFacilityName}_${dateToApiCall.month}_${dateToApiCall.year}_ub04.zip`
        );
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);

        setSnackbarSuccess(true);
        setSnackbarError(false);
        //format date to snackbar message:
        const selectedMonth = moment(dateToApiCall?.month, 'M').format('MMMM');
        const selectedYear = moment(dateToApiCall?.year, 'YYYY').format('YYYY');

        setSnackbar({
          ...snackbar,
          open: true,
          title: 'UB04 Downloaded!',
          message: `${facilityName} ${selectedMonth} ${selectedYear} UB04 successfully downloaded!`,
        });
      })
      .catch((_error: any) => {
        setSnackbarError(true);
        setSnackbarSuccess(false);
        setSnackbar({
          ...snackbar,
          open: true,
          title: 'Error Downloading UB04',
          message:
            'Something went wrong with downloading the UB04. Please refresh or let us know via Intercom if the issue persists.',
        });
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  };

  const getFacilityName = useCallback(() => {
    const facility = getAssignedFacilities.response?.data?.data?.find((fac: any) => {
      if (fac.uuid === getFacilityUuid) {
        return fac;
      }
    });

    return facility?.name ?? '';
  }, [getAssignedFacilities, getFacilityUuid]);

  useEffect(() => {
    const name = getFacilityName();
    setFacilityName(name);
    return () => {
      setFacilityName('');
    };
  }, [getFacilityName]);

  useEffect(() => {
    const selectedPatientValues = patientsFromBatch.map(
      (patient: { label: string; value: unknown }) => patient.value
    );
    setPatientValues(selectedPatientValues);

    return () => {
      setPatientValues([]);
    };
  }, [patientsFromBatch]);

  const handleScroll = () => {
    // If there are more items to load, fetch next page
    const processedSearchTerm = searchString?.trim()?.split('-')?.join('');
    const searchParam =
      processedSearchTerm && processedSearchTerm.length >= 2 ? processedSearchTerm : undefined;
    const hasMore = currentPage * PAGE_SIZE < totalItems;
    if (!hasMore || retrieveBatchLoading) return;

    const nextPage = currentPage + 1;
    getBatch(
      dateToApiCall?.year ?? '',
      dateToApiCall?.month ?? '',
      searchParam,
      nextPage,
      PAGE_SIZE
    );
  };

  const handleDropdownClose = () => {
    // Clear debounced search
    if (patientsFromBatch.length === 0 && searchString.length > 0) {
      debouncedSearchRef.current.cancel();
      setSearchString('');
    }
  };

  // Get whether there are more results available from the API
  const shouldRunApiCall = currentPage * PAGE_SIZE < totalItems;

  const renderPatientsDropdownLabelPlaceholder = (): string => {
    if (isAllPatientsSelected) {
      // Count only the currently displayed patients that are selected
      const selectedDisplayedCount = patientsFromBatch.filter((patient) =>
        patientsParam.split(',').includes(String(patient.value))
      ).length;

      // Only show "All Patients" if there are multiple items or if it was explicitly selected
      if (patientsFromBatch.length > 1) {
        if (selectedDisplayedCount === patientsFromBatch.length && patientsFromBatch.length > 0) {
          return `All Patients (${totalItems})`;
        } else if (selectedDisplayedCount > 0) {
          return `Selected Patients (${selectedDisplayedCount})`;
        } else {
          return 'All Patients';
        }
      } else {
        // For single item, treat it as individual selection
        const selectedValues = patientsFromBatch.filter((patient) =>
          patientsParam.split(',').includes(String(patient.value))
        );
        const selectedLabels = selectedValues.map((patient) => patient.label).join(', ');
        const areLabelsSelected = selectedLabels
          ? `(${selectedValues.length}) ${selectedLabels}`
          : null;
        return areLabelsSelected || 'Patients';
      }
    } else {
      const selectedValues = patientsFromBatch.filter((patient) =>
        patientsParam.split(',').includes(String(patient.value))
      );

      const selectedLabels = selectedValues.map((patient) => patient.label).join(', ');
      const areLabelsSelected = selectedLabels
        ? `(${selectedValues.length}) ${selectedLabels}`
        : null;
      return areLabelsSelected || 'Patients';
    }
  };

  const getStatusMessage = (): string => {
    // If no reports available or date not set
    if (
      !dateToApiCall.year ||
      !dateToApiCall.month ||
      patientValues.length === 0 ||
      !lastUpdatedDate
    ) {
      return 'No UB04 reports available for the selected month.';
    }

    // Check if selected month is current or previous month (within 1 month)
    const selectedDate = moment(`${dateToApiCall.year}-${dateToApiCall.month}`, 'YYYY-M').startOf(
      'month'
    );
    const currentDate = moment().startOf('month');
    const monthsDiff = currentDate.diff(selectedDate, 'months', true);

    // If current or previous month (0-1 months ago)
    if (monthsDiff <= 1) {
      return `Report last refreshed on: ${lastUpdatedDate}. Scheduled to refresh again tonight.`;
    }

    // If 2+ months ago
    return `Report last refreshed on: ${lastUpdatedDate}. If more recent payer or documentation changes were made, submit a ticket to refresh this report.`;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: '100px',
      }}
    >
      <Box sx={{ flex: 1 }}>{/* Content area - can be empty or contain description */}</Box>

      <Box sx={{ mt: 'auto', pt: 2 }}>
        <Grid
          container
          spacing={2}
          alignItems="flex-end"
          direction={{ xs: 'column', md: 'row' }}
        >
          <Grid
            item
            xs={12}
            sm={12}
            md={4}
            sx={{ width: { xs: '100%', md: 'auto' } }}
          >
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 500 }}
            >
              Month
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                views={['month', 'year']}
                value={defaultDate}
                onChange={handleChangeDate}
                maxDate={new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                    size: 'small',
                    sx: {
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                        minHeight: '40px',
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            md={4}
            sx={{ width: { xs: '100%', md: 'auto' } }}
          >
            <Typography
              variant="body2"
              sx={{ mb: 1, fontWeight: 500 }}
            >
              Patients
            </Typography>
            <MultiSelectWithSearch
              sx={{
                border: '1px solid #E5E7EB',
                width: { xs: '100%', md: '200px' },
                minWidth: { xs: '100%', md: '200px' },
                boxShadow: '0px 3px 14px 0px rgba(0, 0, 0, 0.08)',
                display: 'flex',
                justifyContent: 'start',
                height: '40px',
                minHeight: '40px',
                '& .MuiButton-root': {
                  width: '100%',
                  justifyContent: 'space-between',
                },
              }}
              label={renderPatientsDropdownLabelPlaceholder()}
              options={patientsFromBatch}
              value={patientValues}
              selectAll
              selectAllLabel="All Patients"
              withSearch
              searchString={searchString}
              setSearchString={setSearchString}
              onScroll={handleScroll}
              loading={searchLoading || retrieveBatchLoading}
              shouldRunApiCall={shouldRunApiCall}
              isAllSelected={isAllPatientsSelected}
              setIsAllSelected={setIsAllPatientsSelected}
              setSelectedValuesString={setPatientsParam}
              resetValues={resetValues}
              noResultsText="No patients found"
              onClose={handleDropdownClose}
            />
          </Grid>

          <Grid
            item
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              alignItems: 'flex-end',
            }}
          >
            <LoadingButton
              disabled={
                downloadLoading ||
                patientValues.length === 0 ||
                (!isAllPatientsSelected && !patientsParam.length)
              }
              startIcon={<FileDownloadOutlined />}
              variant="contained"
              size="small"
              sx={{
                whiteSpace: 'nowrap',
                height: 35,
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: 120 },
                ':hover': {
                  background: '#6366F1',
                },
                '.MuiLoadingButton-loadingIndicator': {
                  left: 15,
                },
              }}
              loadingPosition="start"
              onClick={handleDownload}
              loading={downloadLoading}
            >
              Download
            </LoadingButton>
          </Grid>
        </Grid>

        {/* Status text and skeleton below form elements */}
        <Box sx={{ mt: 2 }}>
          {retrieveBatchLoading ? (
            <Box sx={{ marginTop: 2 }}>
              <Skeleton
                animation="wave"
                width={'20%'}
              />
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{ marginTop: 2 }}
            >
              {getStatusMessage()}
            </Typography>
          )}
        </Box>
      </Box>

      <Modal open={snackbar.open}>
        <SucessModal
          close={() => setSnackbar({ ...snackbar, open: false })}
          title={snackbar.title}
          message={snackbar.message}
          isSucess={snackbarSuccess && !snackbarError}
        />
      </Modal>
    </Box>
  );
}

export default Ubo4Download;
